/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, SxProps, Typography } from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid-premium";
import { Fetching } from "components/common/loading/fetching";
import { LoadedContent } from "components/common/loading/loadedContent";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { MultiSelect } from "components/common/inputs/multiSelect";
import { SelectBox } from "components/strategyDashboard/Selectbox";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useDateRange } from "contexts/DateRangeContext";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useOutlierHeats } from "./OutlierHeatsContext";
import {
  ConsumptionSourceName,
  StrategyDashboardComplianceData,
  UnitType,
} from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import theme from "src/theme";
import { ColouredCell } from "../ColouredCell";
import { ToggleSelector } from "../ToggleSelector";
import { defaultTableSx } from "../tableFormatting";
import {
  useComplianceUnitsFormatter,
  useStrategyDashboardCompliance,
} from "./complianceHooks";
import {
  buildDiffSourceReferenceMap,
  calculateComplianceScore,
  calculateDiff,
  getMaxDiff,
  getTotalMassFractionDiff,
} from "./utils";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { sorted } from "helpers";

interface ComplianceConsumptionValue {
  unit: UnitType;
  value: number;
}

type ComplianceRowSourceData = Record<
  ConsumptionSourceName,
  ComplianceConsumptionValue[]
>;

interface ComplianceRow {
  id: string;
  materialName: string;
  sourceData: ComplianceRowSourceData;
  outlierHeatIds: string[];
}

export const BucketChargeComplianceTable = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();

  const complianceData = useStrategyDashboardCompliance(start, end);
  const fetching =
    complianceData.status === "success" ? complianceData.fetching : false;

  return (
    <LoadedContent data={complianceData}>
      {(data) => (
        <BucketChargeComplianceTableContent
          complianceData={data}
          fetching={fetching}
        />
      )}
    </LoadedContent>
  );
};

interface BucketChargeComplianceTableContentProps {
  complianceData: StrategyDashboardComplianceData;
  fetching: boolean;
}

const DEFAULT_COMPLIANCE_UNIT: UnitType = "mass_fraction";

const BucketChargeComplianceTableContent = ({
  complianceData,
  fetching,
}: BucketChargeComplianceTableContentProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const showToggles =
    complianceData.available_recipe_compliance_diff_sources.length > 1;

  const [selectedProductGroup, setSelectedProductGroup] =
    useStringQueryParamState<string>("productGroup", "All");
  const [selectedCrew, setSelectedCrew] = useStringQueryParamState<string>(
    "crew",
    "All"
  );

  const defaultDiffSource =
    complianceData.available_recipe_compliance_diff_sources[0] ?? "None";
  const [diffSourceParam, setSelectedDiffSource] = useStringQueryParamState<
    string | null
  >("complianceDiffSource", null);
  const selectedComplianceDiffSource =
    diffSourceParam &&
    complianceData.available_recipe_compliance_diff_sources.includes(
      diffSourceParam
    )
      ? diffSourceParam
      : defaultDiffSource;

  const [unitParam, setSelectedUnit] = useStringQueryParamState<string | null>(
    "complianceUnit",
    null
  );
  const selectedUnit: UnitType =
    unitParam &&
    (complianceData.available_units as string[]).includes(unitParam)
      ? (unitParam as UnitType)
      : DEFAULT_COMPLIANCE_UNIT;

  const visibleUnits = useMemo(
    () =>
      showToggles
        ? complianceData.available_units.filter((unit) => unit === selectedUnit)
        : complianceData.available_units,
    [complianceData.available_units, selectedUnit, showToggles]
  );

  const diffSourceReferenceLabels = useMemo(
    () => buildDiffSourceReferenceMap(complianceData.values),
    [complianceData.values]
  );

  const filteredData = useMemo(() => {
    return {
      ...complianceData,
      values: complianceData.values.filter((value) => {
        const matchesCrew = value.crew_id === selectedCrew;
        const matchesProduct =
          value.product_group_name === selectedProductGroup;
        const matchesComplianceDiffSource =
          value.recipe_compliance_diff_source === selectedComplianceDiffSource;
        return matchesCrew && matchesProduct && matchesComplianceDiffSource;
      }),
    };
  }, [
    complianceData,
    selectedCrew,
    selectedProductGroup,
    selectedComplianceDiffSource,
  ]);

  const allMaterialNames = useMemo(
    () =>
      sorted(
        Array.from(
          new Set(
            filteredData.values.flatMap((v) =>
              v.consumption.map((c) => c.material_name)
            )
          )
        ),
        (name) => name
      ),
    [filteredData]
  );

  const [selectedMaterials, setSelectedMaterials] = useState(allMaterialNames);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets selection when data changes; user also selects
    setSelectedMaterials(allMaterialNames);
  }, [allMaterialNames]);

  const materialFilteredData = useMemo((): StrategyDashboardComplianceData => {
    const selectedSet = new Set(selectedMaterials);
    return {
      ...filteredData,
      values: filteredData.values.map((value) => {
        const filteredConsumption = value.consumption.filter((c) =>
          selectedSet.has(c.material_name)
        );
        const recalculatedTotals = value.totals.map((total) => {
          const sum = filteredConsumption.reduce((acc, c) => {
            const q = c.quantities.find((q) => q.unit === total.unit);
            return acc + (q?.value ?? 0);
          }, 0);
          return { unit: total.unit, value: sum };
        });
        return {
          ...value,
          consumption: filteredConsumption,
          totals: recalculatedTotals,
        };
      }),
    };
  }, [filteredData, selectedMaterials]);

  const rows = useMemo(
    () => buildRows(materialFilteredData),
    [materialFilteredData]
  );

  const totalsRow = useMemo(
    () => buildTotalsRow(materialFilteredData, t),
    [materialFilteredData, t]
  );

  const { openOutlierHeats } = useOutlierHeats();

  const handleHeatIdsClick = (heatIds: string[], materialName: string) => {
    openOutlierHeats(heatIds, materialName);
  };

  const { columns, columnGroupingModel } = useBuildColumns(
    materialFilteredData,
    visibleUnits,
    t,
    handleHeatIdsClick
  );

  return (
    <Box
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-end"
        sx={{ width: "50vw" }}
        mt={2}
      >
        <DateRangeSelector />
        {showToggles ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleSelector<string>
              selectedValue={selectedComplianceDiffSource}
              setSelectedValue={setSelectedDiffSource}
              availableValueLabels={Object.fromEntries(
                complianceData.available_recipe_compliance_diff_sources.map(
                  (diffSource) => [
                    diffSource,
                    diffSourceReferenceLabels.get(diffSource) ?? diffSource,
                  ]
                )
              )}
            />
            <ToggleSelector<"mass" | "mass_fraction">
              selectedValue={selectedUnit as "mass" | "mass_fraction"}
              setSelectedValue={setSelectedUnit}
              availableValueLabels={{
                mass: "mass",
                mass_fraction: "percentage",
              }}
            />
          </Stack>
        ) : null}
      </Stack>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "50vw",
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
            flex: 1,
            "& > .MuiFormControl-root": { flex: 1, minWidth: 160 },
          }}
        >
          <SelectBox
            id="product-group-select"
            labelId="product-group-label"
            label={t("productGroup")}
            availableOptions={complianceData.available_product_groups}
            selectedOption={selectedProductGroup}
            onOptionChange={setSelectedProductGroup}
          />
          <SelectBox
            id="crew-select"
            labelId="crew-label"
            label={t("crew")}
            availableOptions={complianceData.available_crews}
            selectedOption={selectedCrew}
            onOptionChange={setSelectedCrew}
          />
          <MultiSelect
            options={allMaterialNames}
            values={selectedMaterials}
            setValues={setSelectedMaterials}
            label={t("material")}
            getOptionLabel={(name) => name}
            getOptionId={(name) => name}
            small
            selectAll
            labelAbove
            limitTags={1}
          />
        </Box>
        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 1,
            p: 2,
            minWidth: 160,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {t("complianceScore")}
          </Typography>
          <Typography variant="h2" color="primary">
            {calculateComplianceScore(materialFilteredData, t, formatNumber)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: "50vw" }}>
        <Fetching fetching={fetching}>
          <HotkeyCompliantDataGrid
            sx={complianceTableSx}
            rows={rows}
            pinnedRows={{ bottom: [totalsRow] }}
            columns={columns}
            columnGroupingModel={columnGroupingModel}
            getRowClassName={(params) =>
              params.row.id === "total" ? "total-row" : ""
            }
            getCellClassName={(params: GridCellParams<ComplianceRow>) =>
              params.row.id === "total" ? "total-cell" : ""
            }
          />
        </Fetching>
      </Box>
    </Box>
  );
};

const complianceTableSx: SxProps = {
  ...defaultTableSx,
  [".MuiDataGrid-row.total-row"]: {
    borderTop: `1px solid ${theme.palette.grey[600]}`,
  },
  [".MuiDataGrid-cell.total-cell"]: {
    fontWeight: "fontWeightBold",
  },
};

const useBuildColumns = (
  complianceData: StrategyDashboardComplianceData,
  visibleUnits: UnitType[],
  t: TFunction,
  onHeatIdsClick?: (heatIds: string[], materialName: string) => void
): {
  columns: GridColDef<ComplianceRow>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const bracketedUnits = useUnitsFormatter(true);
  const units = useComplianceUnitsFormatter();
  const materialDisplayName = useMaterialDisplayName();

  const columns: GridColDef<ComplianceRow>[] = [
    {
      field: "materialName",
      headerName: t("material_name"),
      flex: 2,
      valueFormatter: (value: string) => materialDisplayName(value),
    },
  ];

  const columnGroupingModel: GridColumnGroupingModel = [];

  const sources = Array.from(
    new Set(
      [
        complianceData.values[0]?.source_a,
        complianceData.values[0]?.source_b,
      ].filter((s): s is ConsumptionSourceName => !!s)
    )
  );

  visibleUnits.forEach((unit) => {
    sources.forEach((source) => {
      columns.push({
        field: `${source}_${unit}`,
        headerName: t(source),
        type: "number",
        flex: 1,
        align: "center",
        valueGetter: (_, row): string => {
          const values = row.sourceData[source];
          if (!values) {
            return units(0, unit);
          }
          return units(values.find((v) => v.unit === unit)?.value ?? 0, unit);
        },
      });
    });

    columns.push({
      field: `${unit}_diff`,
      headerName: t("difference"),
      type: "number",
      flex: 1,
      align: "center",
      cellClassName: "coloured-cell",
      renderCell: (params) => {
        return (
          <DiffCell
            params={params}
            unit={unit}
            complianceData={complianceData}
            onHeatIdsClick={onHeatIdsClick}
          />
        );
      },
    });

    columnGroupingModel.push({
      groupId: unit,
      headerName: `${t("consumption")}${bracketedUnits(unit)}`,
      children: [
        ...sources.map((source) => ({ field: `${source}_${unit}` })),
        { field: `${unit}_diff` },
      ],
    });
  });

  return { columns, columnGroupingModel };
};

const buildRows = (
  complianceData: StrategyDashboardComplianceData
): ComplianceRow[] => {
  if (complianceData.values.length === 0) {
    return [];
  }
  const orderedMaterials = sorted(
    complianceData.values.flatMap((v) =>
      v.consumption.map(({ material_name }, index) => ({
        material_name,
        index,
      }))
    ),
    ({ index }) => index,
    true
  );
  const materialNames = sorted(
    Array.from(
      new Set(
        complianceData.values.flatMap((v) =>
          v.consumption.map((item) => item.material_name)
        )
      )
    ),
    (materialName) =>
      orderedMaterials.find(
        ({ material_name }) => material_name === materialName
      )?.index ?? orderedMaterials.length
  );

  return materialNames.map((materialName) => {
    const outlierHeatIds = Array.from(
      new Set(
        complianceData.values.flatMap((value) => {
          const consumption = value.consumption.find(
            (item) => item.material_name === materialName
          );
          return consumption?.outlier_heat_ids ?? [];
        })
      )
    );

    return {
      id: materialName,
      materialName,
      sourceData: complianceData.values.reduce<ComplianceRowSourceData>(
        (acc, value) => {
          const consumption = value.consumption.find(
            (item) => item.material_name === materialName
          );
          if (!consumption) {
            return acc;
          }
          acc[value.source] = consumption.quantities.map((q) => ({
            unit: q.unit,
            value: q.value,
          }));
          return acc;
        },
        {}
      ),
      outlierHeatIds,
    };
  });
};

const buildTotalsRow = (
  complianceData: StrategyDashboardComplianceData,
  t: TFunction
): ComplianceRow => {
  return {
    id: "total",
    materialName: t("Total"),
    sourceData: complianceData.values.reduce<ComplianceRowSourceData>(
      (acc, value) => {
        acc[value.source] = value.totals.map((q) => ({
          unit: q.unit,
          value: q.value,
        }));
        return acc;
      },
      {}
    ),
    outlierHeatIds: [],
  };
};

const DiffCell = ({
  params,
  unit,
  complianceData,
  onHeatIdsClick,
}: {
  params: GridCellParams<ComplianceRow>;
  unit: UnitType;
  complianceData: StrategyDashboardComplianceData;
  onHeatIdsClick?: (heatIds: string[], materialName: string) => void;
}) => {
  const { t } = useTenantTranslation();
  const units = useComplianceUnitsFormatter();

  const isTotal = params.row.materialName === t("total");
  const outlierHeatIds = params.row.outlierHeatIds ?? [];
  const hasOutlierHeats = outlierHeatIds.length > 0 && !isTotal;

  const content =
    isTotal && unit === "mass_fraction"
      ? units(getTotalMassFractionDiff(complianceData, t), unit)
      : units(
          calculateDiff(params.row.materialName, complianceData, unit, t),
          unit
        );
  const value = Math.abs(
    calculateDiff(params.row.materialName, complianceData, unit, t)
  );
  const maxDiff = getMaxDiff(complianceData, unit, t);
  const normalized = maxDiff > 0 ? value / maxDiff : 0;
  const alpha_ = Math.pow(normalized, 0.5);

  const handleClick = () => {
    if (hasOutlierHeats && onHeatIdsClick) {
      onHeatIdsClick(outlierHeatIds, params.row.materialName);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        width: "100%",
        height: "100%",
        cursor: hasOutlierHeats ? "pointer" : "default",
        "&:hover": hasOutlierHeats
          ? {
              fontWeight: "fontWeightSemiBold",
            }
          : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <ColouredCell
        content={content}
        alpha={params.row.id === "total" ? 0 : alpha_}
        color={theme.palette.data.blue}
        justifyContent="center"
      />
      {hasOutlierHeats ? (
        <NavigateNextIcon
          sx={{
            fontSize: 16,
            position: "absolute",
            right: 4,
            opacity: 0.5,
          }}
        />
      ) : null}
    </Box>
  );
};
